import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Video, Check } from "lucide-react";
import { Reveal, MaskedLine, Marquee } from "@/components/Reveal";
import { usePageTitle } from "@/lib/usePageTitle";
import { ISSUES } from "@/lib/content";

const SESSION_IMG = "https://images.pexels.com/photos/14797780/pexels-photo-14797780.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const ROOM_IMG = "https://images.pexels.com/photos/26932519/pexels-photo-26932519.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const Therapy = () => {
  usePageTitle(
    "Therapy & Counselling",
    "Psychotherapy and counselling for adults: anxiety, depression, grief, burnout, relationships and more — in Marino, Dublin 3 and online worldwide."
  );
  return (
    <div data-testid="therapy-page">
      <section className="mx-auto max-w-6xl px-5 pb-16 pt-16 sm:px-8 lg:pt-24">
        <Reveal y={12}><p className="eyebrow">Therapy & Counselling</p></Reveal>
        <h1 className="mt-6 max-w-3xl font-serif text-4xl font-medium leading-[1.12] tracking-tight text-[#1E2723] sm:text-5xl lg:text-6xl">
          <MaskedLine delay={0.15}>Talking changes things.</MaskedLine>
          <MaskedLine delay={0.3} className="italic text-[#527363]">Here’s how it works.</MaskedLine>
        </h1>
        <Reveal delay={0.5}>
          <p className="mt-7 max-w-2xl text-base leading-relaxed text-[#4A5750] sm:text-lg">
            Psychotherapy and counselling offer a regular, confidential time that is entirely yours — a place to understand what you’re feeling, where it comes from, and how life could feel different.
          </p>
        </Reveal>
      </section>

      <Marquee items={["Anxiety", "Low mood", "Grief & loss", "Burnout", "Relationships", "Life transitions", "Self-esteem"]} />

      <section className="mx-auto max-w-6xl px-5 py-24 sm:px-8" aria-labelledby="approach-heading">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <p className="eyebrow">The approach</p>
            <h2 id="approach-heading" className="mt-4 font-serif text-2xl font-medium leading-snug text-[#1E2723] sm:text-3xl lg:text-4xl">
              Integrative, human and unhurried
            </h2>
            <p className="mt-5 text-base leading-relaxed text-[#4A5750]">
              Brian works integratively, drawing on humanistic, person-centred and existential approaches. In plain terms, that means therapy is built around you — your history, your relationships, and what you want to be different.
            </p>
            <p className="mt-4 text-base leading-relaxed text-[#4A5750]">
              Some sessions are about what is happening right now; others gently explore older patterns. You set the pace, and the work is reviewed together as it unfolds.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="overflow-hidden rounded-2xl border border-[#E2DACD]">
              <img src={SESSION_IMG} alt="Calm, supportive therapy conversation" className="h-96 w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#F2ECE1] py-24" aria-labelledby="issues-list-heading">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow">Reasons adults come</p>
            <h2 id="issues-list-heading" className="mt-4 max-w-2xl font-serif text-2xl font-medium leading-snug text-[#1E2723] sm:text-3xl lg:text-4xl">
              Issues therapy can support you with
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {ISSUES.map((issue, i) => (
              <Reveal key={issue.title} delay={0.05 * i}>
                <div className="soft-card h-full p-6" data-testid={`therapy-issue-card-${i}`}>
                  <span className="block h-1 w-8 rounded-full bg-[#B85C43]" />
                  <h3 className="mt-4 text-lg font-semibold text-[#1E2723]">{issue.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#54645B]">{issue.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-24 sm:px-8" aria-labelledby="formats-heading">
        <Reveal>
          <p className="eyebrow">In person or online</p>
          <h2 id="formats-heading" className="mt-4 max-w-2xl font-serif text-2xl font-medium leading-snug text-[#1E2723] sm:text-3xl lg:text-4xl">
            Two ways to attend — the same quality of care
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-6 lg:grid-cols-2">
          <Reveal delay={0.1}>
            <div className="soft-card h-full overflow-hidden" data-testid="format-in-person">
              <div className="h-56 overflow-hidden">
                <img src={ROOM_IMG} alt="Warm counselling room at the Marino practice" className="h-full w-full object-cover" />
              </div>
              <div className="p-7">
                <span className="inline-flex items-center gap-2 rounded-full bg-[#E3ECE7] px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wider text-[#527363]">
                  <MapPin size={13} /> In person · Marino, Dublin 3
                </span>
                <ul className="mt-5 space-y-3 text-sm leading-relaxed text-[#54645B]">
                  <li className="flex gap-2.5"><Check size={16} className="mt-0.5 shrink-0 text-[#527363]" /> A quiet, private room dedicated to your session</li>
                  <li className="flex gap-2.5"><Check size={16} className="mt-0.5 shrink-0 text-[#527363]" /> Time away from home and work demands</li>
                  <li className="flex gap-2.5"><Check size={16} className="mt-0.5 shrink-0 text-[#527363]" /> Located in Marino, Dublin 3 — directions shared when you book</li>
                </ul>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="soft-card h-full overflow-hidden" data-testid="format-online">
              <div className="flex h-56 items-center justify-center bg-[#527363]">
                <Video size={56} className="text-[#FAF7F2]/90" strokeWidth={1.25} />
              </div>
              <div className="p-7">
                <span className="inline-flex items-center gap-2 rounded-full bg-[#F8EBE7] px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wider text-[#B85C43]">
                  <Video size={13} /> Online · Worldwide
                </span>
                <ul className="mt-5 space-y-3 text-sm leading-relaxed text-[#54645B]">
                  <li className="flex gap-2.5"><Check size={16} className="mt-0.5 shrink-0 text-[#527363]" /> Secure video sessions from your own private space, anywhere in the world</li>
                  <li className="flex gap-2.5"><Check size={16} className="mt-0.5 shrink-0 text-[#527363]" /> Ideal if you are studying away, travelling, or living abroad as part of the Irish diaspora</li>
                  <li className="flex gap-2.5"><Check size={16} className="mt-0.5 shrink-0 text-[#527363]" /> The same warmth, attention and confidentiality as in person</li>
                </ul>
              </div>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.25}>
          <div className="mt-12 text-center">
            <Link to="/booking" data-testid="therapy-book-cta" className="btn-primary">
              Book your first session <ArrowRight size={16} />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Therapy;
